// Standard status pages — NotFound, ErrorBoundary, Pending. Used by
// TanStack Router for unmatched routes / route errors / route transitions.
//
// Visual rules from reference_brand_system.md:
//   - Dark canvas, Nexus Red accent only
//   - Eyebrow pill: mono 11px uppercase tracking 0.16em red on red-muted
//   - Plus Jakarta Sans display for the headline number/title
//   - Inter for the body copy
//
// All three share a centred-card layout for consistency. Strings are
// fed in by the caller via t() so we keep i18n parity (TR + EN).

import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Logo } from "./Logo";
import { Button } from "./Button";
import { useI18n } from "../lib/i18n";

function CenteredFrame({
  eyebrow,
  display,
  title,
  subtitle,
  actions,
}: {
  eyebrow: string;
  display?: ReactNode;
  title: ReactNode;
  subtitle: string;
  actions: ReactNode;
}) {
  return (
    <main className="min-h-full grid place-items-center px-4 py-12">
      <div className="w-full max-w-lg flex flex-col gap-8 text-center">
        <div className="flex justify-center"><Logo size="md" /></div>
        <div className="flex flex-col gap-4 items-center">
          <span className="eyebrow eyebrow-pill inline-flex">{eyebrow}</span>
          {display ? (
            <div className="font-display font-bold text-7xl sm:text-8xl tracking-tight">
              {display}
            </div>
          ) : null}
          <h1 className="font-display text-2xl sm:text-3xl font-semibold mt-2">{title}</h1>
          <p className="text-secondary text-sm max-w-md">{subtitle}</p>
        </div>
        <div className="flex justify-center gap-3 flex-wrap">{actions}</div>
      </div>
    </main>
  );
}

// ----- 404 -----

export function NotFoundPage() {
  const { t } = useI18n();
  return (
    <CenteredFrame
      eyebrow={t("notfound.eyebrow")}
      display={
        <>
          <span className="logo-accent">4</span>0
          <span className="logo-accent">4</span>
        </>
      }
      title={t("notfound.title")}
      subtitle={t("notfound.subtitle")}
      actions={
        <Link to="/">
          <Button variant="primary">{t("notfound.cta")}</Button>
        </Link>
      }
    />
  );
}

// ----- Runtime error fallback -----
// TanStack Router calls this with { error, reset } when a route component
// throws during render or a loader rejects. `reset` invalidates the route
// and lets the user retry without a full page refresh.

export function ErrorPage({ reset }: { error?: Error; reset?: () => void }) {
  const { t } = useI18n();
  return (
    <CenteredFrame
      eyebrow={t("error.eyebrow")}
      title={t("error.title")}
      subtitle={t("error.subtitle")}
      actions={
        <>
          <Button
            variant="primary"
            onClick={() => {
              if (reset) reset();
              else window.location.reload();
            }}
          >
            {t("error.refresh")}
          </Button>
          <Link to="/">
            <Button variant="secondary">{t("error.toHome")}</Button>
          </Link>
        </>
      }
    />
  );
}

// ----- Pending state during route transitions -----

export function PendingPage() {
  return (
    <main className="min-h-full grid place-items-center px-4">
      <div className="flex flex-col items-center gap-3 text-muted">
        <Logo size="md" />
        <span
          aria-hidden
          className="inline-block w-4 h-4 rounded-full border-2 border-border-default border-t-red animate-spin"
        />
      </div>
    </main>
  );
}
